import Head from "next/head";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function Terms() {
  return (
    <>
      <Head>
        <title>Terms of Service - ClearCut</title>
        <meta name="description" content="ClearCut Terms of Service - Read our terms and conditions for using the ClearCut background removal service." />
      </Head>
      <div className="min-h-screen bg-bg text-white flex flex-col">
        <Navbar />

        <motion.main
          className="mx-auto max-w-3xl flex-1 px-6 pb-20 pt-16"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="font-display text-4xl font-extrabold">Terms of Service</h1>
          <p className="mt-2 text-sm text-muted">Last updated: February 15, 2026</p>

          <div className="mt-10 space-y-8 text-sm leading-relaxed text-muted">
            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">1. Acceptance of Terms</h2>
              <p>
                By accessing or using ClearCut (&quot;the Service&quot;), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">2. Description of Service</h2>
              <p>
                ClearCut provides an AI-powered image background removal service. Users can upload images, have backgrounds automatically removed, and download the processed results. The Service is available in free and paid tiers with different usage limits.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">3. User Accounts</h2>
              <p>
                You must create an account to use the Service. You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account. You must provide accurate, current, and complete information during registration.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">4. Acceptable Use</h2>
              <p>You agree not to:</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>Upload illegal, harmful, or offensive content</li>
                <li>Use the Service to infringe on intellectual property rights</li>
                <li>Attempt to circumvent usage limits or rate limits</li>
                <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                <li>Use automated tools to access the Service without authorization</li>
                <li>Resell or redistribute the Service without permission</li>
              </ul>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">5. Image Rights</h2>
              <p>
                You retain all ownership rights to images you upload. By using the Service, you grant ClearCut a limited license to process your images solely for the purpose of providing the background removal service. We do not claim ownership of your content.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">6. Payments and Subscriptions</h2>
              <p>
                Paid plans are billed monthly through Stripe. You can cancel your subscription at any time through the customer portal. Refunds are handled on a case-by-case basis. Usage limits reset on the first of each month.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">7. Service Availability</h2>
              <p>
                We strive to maintain high availability but do not guarantee uninterrupted access. We reserve the right to modify, suspend, or discontinue the Service at any time with reasonable notice.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">8. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, ClearCut shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service. Our total liability shall not exceed the amount you paid for the Service in the 12 months preceding the claim.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">9. Termination</h2>
              <p>
                We may terminate or suspend your account if you violate these Terms. You may delete your account at any time. Upon termination, your data will be deleted in accordance with our Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">10. Changes to Terms</h2>
              <p>
                We may update these Terms from time to time. Continued use of the Service after changes constitutes acceptance of the revised Terms. We will notify users of material changes via email or in-app notification.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">11. Contact</h2>
              <p>
                For questions about these Terms, please contact us at support@clearcut.io.
              </p>
            </section>
          </div>
        </motion.main>

        <Footer />
      </div>
    </>
  );
}
